import React, { useState, useEffect, useRef } from 'react';
import {
    Breadcrumb, Layout, Space, Button, Table, Checkbox, message, Spin, Tag, Typography, Modal, List, Alert as AntAlert,
    Col,
    Row,
    Card, // Import Card component
    Input
} from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Headerdiv from "../layout/header.js";
import { useNavigate } from 'react-router-dom';
import Footerdiv from "../layout/footer.js";
import { AlertError, AlertInfo, AlertSuccess } from '../usableclasses.js';
import { Link } from 'react-router-dom'; // Import Link for quick links
import BulkOrderPrintDocument from './BulkOrderPrint.js'; // Ensure this path is correct

const { Content } = Layout;

const NeedToProcess = () => {
    const { Text } = Typography;
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const navigate = useNavigate();


    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    // State for the processing modal
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isProcessingInModal, setIsProcessingInModal] = useState(false);
    const [processedOrdersInModal, setProcessedOrdersInModal] = useState([]);
    const [processingErrorInModal, setProcessingErrorInModal] = useState(null); // State to hold table filters
    const [tableFilters, setTableFilters] = useState({});

    // State for table search
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN }
            const res = await axios.get(`${ApiURL}/warehouse/allpaidforprocess`, { headers });
            const data = res.data;

            setOrders(data || []);
            setSelectedOrderIds([]);
        } catch (error) {
            AlertError("Failed to fetch orders.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleSelect = (orderIdToToggle) => {
        if (!orderIdToToggle) return; // Do nothing if orderId is null or undefined

        if (selectedOrderIds.includes(orderIdToToggle)) {
            setSelectedOrderIds(prev => prev.filter(id => id !== orderIdToToggle));
        } else {
            if (selectedOrderIds.length >= 10) {
                message.warning("You can only select up to 10 orders.");
                return;
            }
            setSelectedOrderIds(prev => [...prev, orderIdToToggle]);
        }
    };

    const handleSelectTop = (count) => {
        const topOrderIds = orders
            .slice(0, count) // 1. Slices the array to get the top 'count' orders (e.g., 20)
            .map(o => o.orderId) // 2. Extracts the orderId from each
            .filter(id => id != null) // 3. Filters out any null or undefined IDs (using filter(Boolean) in processing.js is equivalent for this)
            .slice(0, count); // 4. CRITICAL STEP: Takes only the first 10 from the result of step 3
        setSelectedOrderIds(topOrderIds);
    };

    const handleOpenProcessModal = () => {
        if (selectedOrderIds.length === 0) {
            AlertError("No orders selected.");
            return;
        }
        setIsModalVisible(true);
        setProcessedOrdersInModal([]);
        setProcessingErrorInModal(null);
        processOrdersInModal(); // Start processing when modal opens
    };

    const processOrdersInModal = async () => {
        // Safeguard: Ensure no more than 10 orders are processed, though UI should prevent this.
        if (selectedOrderIds.length > 10) {
            const errorMsg = "Cannot process more than 10 orders at a time. Please select up to 10 orders.";
            AlertError(errorMsg);
            setProcessingErrorInModal(errorMsg); // Display error in the modal
            setIsProcessingInModal(false);      // Stop modal's own loading indicator
            return;                             // Prevent API call
        }

        setIsProcessingInModal(true);
        setProcessingErrorInModal(null); // Clear any previous error shown in modal

        try {
            const headers = { SystemAccessToken: TOKEN };
            const payload = {
                orderIds: selectedOrderIds.join(','),
                processRequestBy: UPDATED_BY
            };

            const processResponse = await axios.post(`${ApiURL}/warehouse/bulksendtoprocess`, payload, { headers });
            const ordersFromServer = processResponse.data || [];

            if (ordersFromServer.length === 0) {
                AlertInfo("No orders were processed or no data returned by the server.");
                setProcessedOrdersInModal([]);
            } else {
                setProcessedOrdersInModal(ordersFromServer);
                AlertSuccess(`${ordersFromServer.length} order(s) processed successfully!`);
                // Optionally, refresh the main list of orders after processing
                fetchOrders();
                setSelectedOrderIds([]); // Clear selection after successful processing
            }
        } catch (error) {
            let messageText = "An error occurred while processing orders.";
            if (error.response) {
                messageText = error.response.data?.message || `Error ${error.response.status}: Processing failed.`;
            } else if (error.request) {
                messageText = "No response from server during processing. Please check your network connection.";
            } else {
                messageText = error.message || "Unexpected error during processing.";
            }
            AlertError(messageText);
            setProcessingErrorInModal(messageText);
            setProcessedOrdersInModal([]);
        } finally {
            setIsProcessingInModal(false);
        }
    };

    const handlePrintInModal = async () => {
        if (!processedOrdersInModal.length) {
            AlertError("No processed orders available to print.");
            return;
        }
        // CRITICAL: Log the data being passed to the PDF generator.
        // Check your browser's developer console for this output.
        console.log("Attempting to print. Data for PDF (processedOrdersInModal):", JSON.stringify(processedOrdersInModal, null, 2));
        // Optional: Add a check for essential fields if you know what they are
        // if (processedOrdersInModal.some(order => !order.orderReferance || !order.itemsList?.length)) { console.warn("Warning: Some orders in processedOrdersInModal might be missing critical data like orderReferance or itemsList."); }
        try {
            const document = <BulkOrderPrintDocument ordersData={processedOrdersInModal} />;
            const pdfBlob = await pdf(document).toBlob();
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(pdfUrl, '_blank');

            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                    URL.revokeObjectURL(pdfUrl);
                };
                printWindow.onclose = () => URL.revokeObjectURL(pdfUrl);
            } else {
                AlertError("Could not open print window. Check popup blocker.");
                URL.revokeObjectURL(pdfUrl);
            }
        } catch (printError) {
            AlertError(`Error generating PDF: ${printError.message}`);
        }
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        confirm({ closeDropdown: true });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />
            ) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    const columns = [
        {
            title: 'Action',
            key: 'action', // Using key as dataIndex is not directly used for value
            render: (text, record) => (
                <Checkbox
                    checked={selectedOrderIds.includes(record.orderId)}
                    onChange={() => handleSelect(record.orderId)}
                    disabled={!record.orderId} // Disable checkbox if orderId is missing
                />
            ),
        },
        {
            title: 'Order No',
            dataIndex: 'orderRef',
            key: 'orderRef',
            ...getColumnSearchProps('orderRef', 'Order No')
        },
        {
            title: 'Date/Time',
            dataIndex: 'orderDateTime',
            key: 'orderDateTime',
            render: (text) => new Date(text).toLocaleString()
        },
        {
            title: 'Platform',
            dataIndex: 'orderPlatform',
            key: 'orderPlatform',
            render: (text) => text ? text.toUpperCase() : 'N/A',
            ...getColumnSearchProps('orderPlatform', 'Platform'),
        },
        {
            title: 'Order Total',
            dataIndex: 'orderCost',
            key: 'orderCost',
            ...getColumnSearchProps('orderCost', 'Order Total')
        },
        {
            title: 'Ship Method',
            dataIndex: 'shippedMethod',
            key: 'shippedMethod',
            ...getColumnSearchProps('shippedMethod', 'Ship Method')
        },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid) => <Tag color={isPaid ? 'green' : 'red'}>{isPaid ? 'Yes' : 'No'}</Tag>,
            filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
            onFilter: (value, record) => record.isPaid === value,
        },
        {
            title: 'Is Cancel',
            dataIndex: 'isCanceled',
            key: 'isCanceled',
            render: (isCanceled) => (
                <Tag color={isCanceled ? 'red' : 'green'}>{isCanceled ? 'Yes' : 'No'}</Tag>
            ),
            filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
            onFilter: (value, record) => record.isCanceled === value,
        },
    ];

    // Handle table change event (pagination, filters, sorter)
    const handleTableChange = (pagination, filters, sorter) => {
        // Ant Design's Table onChange provides the current filters object
        setTableFilters(filters);
        // If you have server-side pagination/sorting/filtering, you would trigger data fetch here
        // e.g., fetchOrders(filters, sorter, pagination);
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({}); // Clear all filters
        setSearchText(''); // Clear any active search text
        setSearchedColumn(''); // Clear any active searched column
    };

    return (
        <Spin spinning={loading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Warehouse</Breadcrumb.Item>
                        <Breadcrumb.Item>To Be Process</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col xs={24} sm={12} md={12}>
                                <Space wrap>
                                    <Tag color="blue">Total To Process: {orders.length}</Tag>
                                    <Tag color="green">Selected: {selectedOrderIds.length}</Tag>
                                    <Tag color="red">Max Selection: 10</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12} md={12} style={{ textAlign: 'right' }}>
                                <Space wrap>
                                    <Button onClick={fetchOrders}>Refresh</Button>
                                    <Button onClick={() => handleSelectTop(10)}>Select Top 10</Button>
                                    <Button
                                        type="primary"
                                        disabled={selectedOrderIds.length === 0}
                                        onClick={handleOpenProcessModal}
                                    >
                                        Send Selected To Process
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Clear All Table Filters
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={orders}
                        columns={columns}
                        rowKey="orderId" // Use orderId as the unique row key
                        scroll={{ x: 'max-content' }} // Make table horizontally scrollable
                        pagination={{
                            // pagination is handled by AntD's internal state, but you can override it here
                            pageSize: 10, // Default page size
                            showSizeChanger: false, // Hide page size changer
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            // You might want to add onChange if you plan to implement server-side pagination
                            // onChange: (page, pageSize) => { /* handle page change */ },
                        }}
                        rowClassName={(record) =>
                            record.orderId && selectedOrderIds.includes(record.orderId) ? 'ant-table-row-selected' : ''
                        }
                        onChange={handleTableChange} // Add onChange handler
                        locale={{
                            emptyText: loading ? 'Fetching orders...' : 'No orders currently need to be processed.'
                        }}
                    />

                    <Modal
                        title="Process Orders Confirmation"
                        open={isModalVisible}
                        onCancel={() => setIsModalVisible(false)}
                        footer={[
                            <Button key="close" onClick={() => setIsModalVisible(false)}>
                                Close
                            </Button>,
                            <Button
                                key="print"
                                type="primary"
                                onClick={handlePrintInModal}
                                disabled={isProcessingInModal || processedOrdersInModal.length === 0}
                                loading={isProcessingInModal && processedOrdersInModal.length > 0} // Show loading on print if still processing but has data (edge case)
                            >
                                Print Orders
                            </Button>,
                        ]}
                        width={600}
                    >
                        {isProcessingInModal && <Spin tip="Processing selected orders..." style={{ display: 'block', textAlign: 'center', marginBottom: '20px' }} />}

                        {!isProcessingInModal && processingErrorInModal && (
                            <AntAlert message="Processing Error" description={processingErrorInModal} type="error" showIcon style={{ marginBottom: 16 }} />
                        )}

                        {!isProcessingInModal && !processingErrorInModal && processedOrdersInModal.length > 0 && (
                            <>
                                <Typography.Title level={5}>Successfully Processed Orders:</Typography.Title>
                                <List
                                    bordered
                                    dataSource={processedOrdersInModal}
                                    renderItem={order => (
                                        <List.Item>
                                            <Tag color="green">{order.orderReferance || 'N/A'}</Tag>
                                        </List.Item>
                                    )}
                                />
                            </>
                        )}
                        {!isProcessingInModal && !processingErrorInModal && processedOrdersInModal.length === 0 && !isProcessingInModal && (
                            <Typography.Text>No orders were processed or selected for processing.</Typography.Text>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default NeedToProcess;
